import { useMutation } from "@tanstack/react-query";

import {
  confirmVerification,
  login,
  resendVerification,
  signup,
  markBusinessOnboardingComplete,
  updatePhone,
  verifyEmailByToken,
  forgotPasswordInit,
  forgotPasswordComplete,
  passwordRecoveryInit,
  passwordRecoveryComplete,
  passwordChangeInit,
  passwordChangeComplete,
} from "../api/authApi";
import { useCustomMutation } from "../queryUtils";

export function useSignupMutation() {
  return useCustomMutation(signup);
}

export function useLoginMutation() {
  return useCustomMutation(login);
}
export function useStartVerificationMutation(type: "email" | "phone") {
  return useMutation<
    {
      success: boolean;
      message?: string;
    },
    Error,
    void
  >({
    mutationFn: () => resendVerification({ type }),
  });
}

export function useConfirmVerificationMutation(type: "email" | "phone") {
  return useMutation<
    {
      success: boolean;
      message?: string;
    },
    Error,
    { code: string }
  >({
    mutationFn: ({ code }) => confirmVerification({ type, code }),
  });
}

export function useUpdatePhoneMutation() {
  return useMutation<
    { success: boolean; message?: string },
    Error,
    { phone: string }
  >({
    mutationFn: ({ phone }) => updatePhone(phone),
  });
}

export function useVerifyEmailByTokenMutation() {
  return useMutation<
    { success: boolean; message?: string },
    Error,
    { token: string }
  >({
    mutationFn: ({ token }) => verifyEmailByToken(token),
  });
}

export function useMarkBusinessOnboardingCompleteMutation() {
  return useMutation({
    mutationFn: markBusinessOnboardingComplete,
  });
}

export function useForgotPasswordInitMutation() {
  return useMutation<
    { success: boolean; message?: string },
    Error,
    { email: string }
  >({
    mutationFn: ({ email }) => forgotPasswordInit(email),
  });
}

export function useForgotPasswordCompleteMutation() {
  return useMutation<
    { success: boolean; message?: string },
    Error,
    { token: string; newPassword: string }
  >({
    mutationFn: (data) => forgotPasswordComplete(data),
  });
}

/** SMS-OTP password recovery (unauthenticated, from the Login screen). */
export function usePasswordRecoveryInitMutation() {
  return useMutation<
    { success: boolean; message?: string },
    Error,
    { email: string }
  >({
    mutationFn: ({ email }) => passwordRecoveryInit(email),
  });
}

export function usePasswordRecoveryCompleteMutation() {
  return useMutation<
    { success: boolean; message?: string },
    Error,
    { email: string; code: string; newPassword: string }
  >({
    mutationFn: (data) => passwordRecoveryComplete(data),
  });
}

/** SMS-OTP password change for the signed-in user (Settings › Personal Information). */
export function usePasswordChangeInitMutation() {
  return useMutation<{ success: boolean; message?: string }, Error, void>({
    mutationFn: () => passwordChangeInit(),
  });
}

export function usePasswordChangeCompleteMutation() {
  return useMutation<
    { success: boolean; message?: string },
    Error,
    { code: string; newPassword: string }
  >({
    mutationFn: (data) => passwordChangeComplete(data),
  });
}
